import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, PageHeader, Table } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useCallback, useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { computeExperienceName } from 'app/components/commons/Experience/utils';
import {
  PUBLISH_ERROR_TYPE,
  PublishModalError,
} from 'app/components/commons/PublishModalError/PublishModalError';
import { PaginatedSearchHeader } from 'app/components/commons/Range/PaginatedSearchHeader/PaginatedSearchHeader';
import { Pagination } from 'app/components/commons/Range/Pagination/Pagination';
import {
  IsLegacyFilterOption,
  ViewModeRadio,
} from 'app/components/commons/ViewModeRadio/ViewModeRadio';
import { CoverCellRoom, PublishedCell } from 'app/components/lists/cells';
import { ModalCheckBox } from 'app/components/pages/Extranet/ExtranetBookings/ModalCheckBox';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useExperiences } from 'app/hooks/data/core/admin/experiences/useExperiences';
import { usePublishExperience } from 'app/hooks/data/core/admin/experiences/usePublishExperience';
import { useUnpublishExperience } from 'app/hooks/data/core/admin/experiences/useUnpublishExperience';
import {
  useExperiencesCategories,
  usePublishOldExperience,
} from 'app/hooks/data/useExperience';
import { EXPERIENCE_STATUS } from 'app/typings/core/admin/experiences/enum/experienceStatus';
import type { ExperienceStatus as ExperienceStatusType } from 'app/typings/core/admin/experiences/enum/experienceStatus';
import type { ExperienceListElement } from 'app/typings/core/admin/experiences/experienceList';
import { dateAndHour } from 'app/utils/dates';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { extractMissingFields } from 'app/utils/publishError';
import {
  extractBoolean,
  parseArrayParam,
  useQueryParamHistory,
} from 'app/utils/queryParamHistory';
import type { SortOrder } from 'app/utils/sort';
import {
  convertFromAntdSortOrder,
  convertToAntdSortOrder,
  formatSortingForAPI,
  getSortOrder,
} from 'app/utils/sort';

import 'styles/layout/page-list.scss';

import { NewExperienceModal } from './NewExperienceModal/NewExperienceModal';

const { Content } = Layout;
const { Column } = Table;

type ExperiencesListQueryParam = {
  search: string;
  pageNumber: number;
  dateSorting: SortOrder;
  sortingColumn?: 'CREATED_AT' | 'UPDATED_AT' | 'NAME';
  isPublishedFilter: boolean[];
  staycationExpFilter: boolean[];
  isLegacyFilter?: IsLegacyFilterOption;
};

const formatIsPublished = (
  isPublishedFilter?: boolean[]
): ExperienceStatusType[] | undefined => {
  if (!isPublishedFilter || isPublishedFilter.length === 0) {
    return undefined;
  }

  const hasPublished = isPublishedFilter.includes(true);
  const hasUnpublished = isPublishedFilter.includes(false);

  if (hasPublished && hasUnpublished) {
    return undefined;
  }

  return isPublishedFilter.map((val) =>
    val ? EXPERIENCE_STATUS.PUBLISHED : EXPERIENCE_STATUS.UNPUBLISHED
  );
};

const formatIsStaycationExp = (staycationExpFilter?: boolean[]) => {
  if (!staycationExpFilter || staycationExpFilter.length > 1) {
    return undefined;
  }

  return staycationExpFilter[0];
};

const queryParamInit = {
  pageNumber: 1,
  dateSorting: 'descend',
  sortingColumn: 'UPDATED_AT',
  isPublishedFilter: [true, false],
  staycationExpFilter: [false, true],
  isLegacyFilter: IsLegacyFilterOption.CURRENT,
} as ExperiencesListQueryParam;
const queryParamKeys = {
  pageNumber: 'p',
  dateSorting: 'ds',
  isPublishedFilter: 'pu',
  sortingColumn: 'sc',
  staycationExpFilter: 'sx',
  search: 's',
  isLegacyFilter: 'il',
};
const queryParamFormatter = {};
const queryParamExtractor = {
  pageNumber: parseInt,
  dateSorting: getSortOrder,
  isPublishedFilter: parseArrayParam(extractBoolean),
  staycationExpFilter: parseArrayParam(extractBoolean),
};

const ExperiencesContent = () => {
  const { data: experienceCategories } = useExperiencesCategories();

  const { mutateAsync: publishOldExperience } = usePublishOldExperience();
  const { mutateAsync: publishExperience } = usePublishExperience();
  const { mutateAsync: unpublishExperience } = useUnpublishExperience();

  const { enterEditMode } = useContext(ViewEditModeContext);

  const [isNewExperienceModalOpen, setIsNewExperienceModalOpen] =
    useState(false);
  const [missingFieldsError, setMissingFieldsError] = useState<string[] | null>(
    null
  );
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const { queryParam, updateQueryParam } =
    useQueryParamHistory<ExperiencesListQueryParam>(
      queryParamInit,
      queryParamKeys,
      queryParamFormatter,
      queryParamExtractor
    );

  const debouncedQueryParams = useDebounce(queryParam, 500);

  const makePayload = useCallback(
    () => ({
      search: debouncedQueryParams.search,
      sortingOrder:
        formatSortingForAPI(debouncedQueryParams.dateSorting) || undefined,
      sortingColumn: debouncedQueryParams.sortingColumn,
      pageNumber: debouncedQueryParams.pageNumber || 1,
      status: formatIsPublished(debouncedQueryParams.isPublishedFilter),
      isStaycationExp: formatIsStaycationExp(
        debouncedQueryParams.staycationExpFilter
      ),
      isLegacyFilter: debouncedQueryParams.isLegacyFilter,
    }),
    [
      debouncedQueryParams.search,
      debouncedQueryParams.dateSorting,
      debouncedQueryParams.sortingColumn,
      debouncedQueryParams.pageNumber,
      debouncedQueryParams.isPublishedFilter,
      debouncedQueryParams.staycationExpFilter,
      debouncedQueryParams.isLegacyFilter,
    ]
  );
  const {
    data: experiences,
    isLoading,
    refetch: reloadExperiences,
  } = useExperiences(makePayload());

  const selectedIsLegacyFilterOption =
    queryParam.isLegacyFilter ?? IsLegacyFilterOption.ALL;

  const handlePageNumber = (pageNumber: number) =>
    updateQueryParam({ pageNumber });

  const handleSearch = (search: string) => {
    updateQueryParam({
      search: search,
      pageNumber: 1,
    });
  };

  const handleStaycationExpFilter = (filters: boolean[]) => {
    updateQueryParam({
      staycationExpFilter: filters,
      pageNumber: 1,
    });
  };

  const renderHeader = () => (
    <PaginatedSearchHeader
      onSearch={handleSearch}
      currentSearch={queryParam.search}
      currentPage={queryParam.pageNumber || 1}
      setPageNumber={handlePageNumber}
      isLastPage={experiences?.meta.isLastPage ?? false}
    />
  );

  const handleIsStaycationExpFilter = (statuses: boolean[]) => {
    if (!statuses.length) {
      handleStaycationExpFilter([false, true]);
    } else {
      handleStaycationExpFilter(statuses);
    }
  };

  const handleIsPublishedFilter = (statuses: boolean[]) => {
    if (!statuses.length) {
      updateQueryParam({ isPublishedFilter: undefined, pageNumber: 1 });
    } else {
      updateQueryParam({ isPublishedFilter: statuses, pageNumber: 1 });
    }
  };

  const goToExperienceEditPage = (id: number, isLegacy: boolean) => {
    const pathname = isLegacy
      ? `/activities/legacy/${id}`
      : `/activities/${id}`;

    history.push({
      pathname,
      state: { pathname: location.pathname + location.search },
    });
  };

  const handleRowActions = ({ id, isLegacy }: ExperienceListElement) => ({
    onClick: () => goToExperienceEditPage(id, isLegacy),
  });

  const actionMenu = (experience: ExperienceListElement) => (
    <div className="actions-menu">
      <Dropdown
        menu={{
          items: [
            {
              key: 'edit',
              label: 'Edit',
              onClick: () =>
                goToExperienceEditPage(experience.id, experience.isLegacy),
            },
            {
              key: 'publish',
              label:
                experience.status === EXPERIENCE_STATUS.PUBLISHED
                  ? 'Unpublish'
                  : 'Publish',
              onClick: async () => {
                try {
                  if (experience.isLegacy) {
                    await publishOldExperience(experience.id);
                  } else if (
                    experience.status === EXPERIENCE_STATUS.PUBLISHED
                  ) {
                    await unpublishExperience({
                      experienceId: experience.id,
                      hotelId: experience.hotelId,
                    });
                  } else {
                    await publishExperience({
                      experienceId: experience.id,
                      hotelId: experience.hotelId,
                    });
                  }

                  await reloadExperiences();
                } catch (err) {
                  const missingFields = extractMissingFields(err);

                  if (missingFields !== null) {
                    setMissingFieldsError(missingFields);
                    setIsErrorModalVisible(true);
                  }
                }
              },
            },
          ],
        }}
        trigger={['click']}
      >
        <EllipsisOutlined rotate={90} />
      </Dropdown>
    </div>
  );

  const newExperienceButton = (
    <>
      <Button
        type="primary"
        size="large"
        onClick={() => {
          enterEditMode();
          setIsNewExperienceModalOpen(true);
        }}
      >
        New
      </Button>
      <NewExperienceModal
        open={isNewExperienceModalOpen}
        onClose={() => setIsNewExperienceModalOpen(false)}
      />
    </>
  );

  const tableOnChange = (
    __: unknown,
    ___: unknown,
    sorter:
      | SorterResult<ExperienceListElement>
      | SorterResult<ExperienceListElement>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const { order, columnKey } = singleSorter;

    updateQueryParam({
      dateSorting: convertFromAntdSortOrder(order),
      sortingColumn: (columnKey as 'UPDATED_AT') ?? 'UPDATED_AT',
      pageNumber: 1,
    });
  };

  const nameColumnRender = (__: unknown, exp: ExperienceListElement) => {
    const coverPicture = exp.coverPicture;

    const expCat = experienceCategories?.find(
      (cat) => cat.id === exp.categoryId
    );

    const experienceName = computeExperienceName({
      ...exp,
      category: expCat,
      quantity: exp.quantity ?? null,
      locations: exp.location,
    });

    return (
      <CoverCellRoom
        category={experienceName ?? ''}
        entity={{
          coverPicture,
        }}
      />
    );
  };

  const experienceTypeColumnTitle = (
    <ModalCheckBox
      title="Type"
      defaultValue={[false, true]}
      onChange={handleIsStaycationExpFilter}
      items={[
        {
          value: false,
          label: 'Hotel',
        },
        {
          value: true,
          label: 'Staycation',
        },
      ]}
      value={queryParam.staycationExpFilter}
    />
  );

  const experienceTypeRender = (__: unknown, exp: ExperienceListElement) => {
    return exp.hotelId ? 'Hotel' : 'Staycation';
  };

  const publishedColumnTitle = (
    <ModalCheckBox
      title="Status"
      defaultValue={[true, false]}
      onChange={handleIsPublishedFilter}
      items={[
        {
          value: true,
          label: 'Published',
        },
        {
          value: false,
          label: 'Unpublished',
        },
      ]}
      value={queryParam.isPublishedFilter}
    />
  );

  return (
    <Layout className="page-list">
      <PageHeader
        className="header"
        title="Activities"
        extra={newExperienceButton}
      />
      <Content className="body">
        <Table<ExperienceListElement>
          dataSource={experiences?.data ?? []}
          pagination={false}
          title={renderHeader}
          loading={isLoading}
          rowKey="id"
          onRow={handleRowActions}
          onChange={tableOnChange}
        >
          <Column
            title="Name"
            dataIndex="displayName"
            render={nameColumnRender}
          />
          <Column title="Hotel" dataIndex="hotelName" />
          <Column
            title={experienceTypeColumnTitle}
            render={experienceTypeRender}
          />
          <Column<ExperienceListElement>
            title="Data Model"
            dataIndex="isLegacy"
            filterDropdown={() => (
              <ViewModeRadio<IsLegacyFilterOption>
                value={selectedIsLegacyFilterOption}
                onChange={(selected) => {
                  updateQueryParam({
                    isLegacyFilter: selected,
                    pageNumber: 1,
                  });
                }}
                options={[
                  {
                    value: IsLegacyFilterOption.CURRENT,
                    label: 'Current hotel mode',
                  },
                  {
                    value: IsLegacyFilterOption.ALL,
                    label: 'All hotel modes',
                  },
                ]}
              />
            )}
            render={(isLegacy: boolean) => (isLegacy ? 'Old' : 'New')}
          />
          <Column
            key="UPDATED_AT"
            title="Last modified"
            dataIndex="updatedAt"
            sorter
            sortOrder={
              queryParam.sortingColumn === 'UPDATED_AT'
                ? convertToAntdSortOrder(queryParam.dateSorting ?? 'none')
                : null
            }
            render={dateAndHour}
          />
          <Column
            title={publishedColumnTitle}
            dataIndex="status"
            render={(
              status: ExperienceStatusType,
              record: ExperienceListElement
            ) => PublishedCell(status === EXPERIENCE_STATUS.PUBLISHED, record)}
          />
          <Column
            render={actionMenu}
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
        </Table>
        <Pagination
          className="footer"
          currentPage={queryParam.pageNumber || 1}
          setPageNumber={handlePageNumber}
          isLastPage={experiences?.meta.isLastPage ?? false}
        />
        <PublishModalError
          type={PUBLISH_ERROR_TYPE.ACTIVITY}
          missingFields={missingFieldsError ?? []}
          isOpen={isErrorModalVisible}
          handleOk={() => setIsErrorModalVisible(false)}
        />
      </Content>
    </Layout>
  );
};

export const Experiences = () => (
  <ViewEditModeContextProvider>
    <ExperiencesContent />
  </ViewEditModeContextProvider>
);
